/**
 * cl-core/lead — 정의문 리드 · 기준일 · 핵심 팩트 행 (지시서 §1-1 · §1-2). 순수 TS.
 *
 * - 정의문 리드: 첫 문단 2문장, 실데이터에서만 파생. meta description 도 같은 문자열.
 * - 기준일 = 원천 데이터 updated_at 의 최대값(오늘 날짜 금지). h1 아래 줄 · JSON-LD dateModified ·
 *   sitemap lastmod 가 모두 `basisDate()` 한 값을 쓴다.
 */
import { kstNow } from './kst';
import { josa } from './josa';
import { nextStageInfo, redevTypeWord, stepperKindOf, type SubPhase } from './stages';
import { cleanCell } from './forbidden';
import { CORE_FACT_KEYS, CORE_FACT_LABEL, type CoreFactKey } from './mode';

/* ───────────── 기준일 ───────────── */

export interface BasisDate {
  /** ISO(UTC) — JSON-LD dateModified · sitemap lastmod */
  iso: string;
  /** KST 「YYYY.MM.DD」 — h1 아래 「기준일 …」 */
  label: string;
  /** KST YYYY, M — title 연·월 토큰 */
  year: number;
  month: number;
}

/**
 * 원천 updated_at 들의 최대값. 유효한 값이 하나도 없으면 null(줄·필드를 내지 않는다).
 * `exclude` 로 일괄 타임스탬프 등 «확인이 아닌 값» 을 뺄 수 있다(상세는 lib/apt/batch-stamp 를 넘긴다).
 */
export function basisDate(values: Array<string | Date | null | undefined>, exclude?: (v: string) => boolean): BasisDate | null {
  let best: number | null = null;
  for (const v of values) {
    if (v == null) continue;
    const s = v instanceof Date ? v.toISOString() : String(v);
    if (!s.trim()) continue;
    if (exclude && exclude(s)) continue;
    const t = new Date(s).getTime();
    if (!Number.isFinite(t)) continue;
    if (best == null || t > best) best = t;
  }
  if (best == null) return null;
  const k = kstNow(best);
  const y = k.getUTCFullYear();
  const m = k.getUTCMonth() + 1;
  const d = k.getUTCDate();
  const p = (n: number) => (n < 10 ? `0${n}` : String(n));
  return { iso: new Date(best).toISOString(), label: `${y}.${p(m)}.${p(d)}`, year: y, month: m };
}

/* ───────────── 정의문 리드 ───────────── */

export interface FillLeadInput {
  name: string;
  sigungu?: string | null;
  dong?: string | null;
  siteType?: string | null;
  projectType?: string | null;
  lifecycleStage?: string | null;
  hasRedevSource?: boolean;
  /** live1-d — 입주월이 지났는가(준공 후 미분양 판정 · stages StageContext). 생략 시 종전과 같다. */
  moveInPast?: boolean;
  /** live2 c 지시 2 — 연결 모집공고 국면(stages subscriptionPhase). 생략 시 종전과 같다. */
  subPhase?: SubPhase | null;
}

/** 기존 아파트(실거래·랜드마크) 현장 — 「진행 중인 분양 사업」이라 쓰지 않는다(live1-d · sweep B·P). */
export function isExistingAptSite(siteType: string | null | undefined, lifecycleStage?: string | null): boolean {
  if (siteType === 'trade' || siteType === 'landmark') return true;
  return siteType !== 'redevelopment' && (lifecycleStage === 'active_trade' || lifecycleStage === 'resale' || lifecycleStage === 'landmark_active');
}

/**
 * 채움 모드 단계 설명형 리드(§1-2 #2):
 * 「○○은 {시군구} {동}에서 추진 중인 {사업유형} 사업으로, 현재 {단계}입니다. 다음 단계는 {다음}이며 통상 {범위}가 걸립니다.」
 * 단계를 판정할 수 없으면 null. 다음 단계가 없으면 두 번째 문장을 뺀다.
 */
export function buildFillLead(i: FillLeadInput): string | null {
  const kind = stepperKindOf(i.siteType, i.hasRedevSource);
  const info = nextStageInfo(i.lifecycleStage, kind, { moveInPast: i.moveInPast, subPhase: i.subPhase });
  if (!info) return null;
  const name = String(i.name || '').trim();
  if (!name) return null;
  const place = [i.sigungu, i.dong].map((s) => String(s ?? '').trim()).filter(Boolean).join(' ');
  // live1-d(sweep B·P) — 기존 아파트는 분양 사업이 아니다: 「…에서 진행 중인 분양 사업으로, 현재 입주 완료입니다」 → 「…에 있는 아파트 단지입니다」.
  if (kind === 'sale' && isExistingAptSite(i.siteType, i.lifecycleStage)) {
    return `${name}${josa(name, '은/는')} ${place ? `${place}에 있는 ` : ''}아파트 단지입니다.`;
  }
  const typeWord = kind === 'redev' ? redevTypeWord(i.projectType, name) : '분양';
  const where = place ? `${place}에서 ` : '';
  const verb = kind === 'redev' ? '추진 중인' : '진행 중인';
  const first = `${name}${josa(name, '은/는')} ${where}${verb} ${typeWord} 사업으로, 현재 ${info.current.label}입니다.`;
  if (!info.next || !info.typicalRange) return first;
  return `${first} 다음 단계는 ${info.next.label}이며 통상 ${info.typicalRange}${josa(info.typicalRange, '이/가')} 걸립니다.`;
}

/* ───────────── 핵심 팩트 행 ───────────── */

export interface FactRow {
  key: CoreFactKey | string;
  label: string;
  value: string;
}

/**
 * 핵심 정보 표(2열) 행. 값이 없거나 금지 문자열이면 «행을 생략» 한다(§1-2 #3).
 * 숫자 세대수는 「1,234세대」로 표기한다. extra 는 8행 뒤에 붙는다(같은 검사).
 */
export function buildFactRows(
  facts: Partial<Record<CoreFactKey, unknown>>,
  extra: FactRow[] = [],
  /** live1-d — 행 라벨 덮어쓰기(지난 입주월 「입주」 · 계획 세대수 「계획 세대」 등). 생략 시 CORE_FACT_LABEL. */
  labels?: Partial<Record<CoreFactKey, string>>,
): FactRow[] {
  const rows: FactRow[] = [];
  for (const k of CORE_FACT_KEYS) {
    let v = facts[k];
    if ((k === 'totalUnits' || k === 'generalUnits') && typeof v === 'number') {
      v = Number.isFinite(v) && v > 0 ? `${Math.round(v).toLocaleString('ko-KR')}세대` : null;
    }
    const c = cleanCell(v, k === 'salePeriod' || k === 'moveIn' ? 'schedule' : 'fact');
    if (c != null) rows.push({ key: k, label: (labels && labels[k] && String(labels[k]).trim()) || CORE_FACT_LABEL[k], value: c });
  }
  for (const r of extra) {
    const c = cleanCell(r.value, 'fact');
    if (c != null && String(r.label || '').trim()) rows.push({ key: r.key, label: r.label, value: c });
  }
  return rows;
}
